import { isLightTheme, resolveSemanticColor } from '@/theme/semanticTokens';
import { fontFamilies, s, type SpacingToken } from '@/theme/tokens';
import { dialogRhythm } from '@/ui/composites/dialogRhythm';
import { Inline } from '@/ui/layout/Inline';
import { Stack, type StackProps } from '@/ui/layout/Stack';
import { Box, type BoxProps } from '@/ui/primitives/Box';
import { List, type ListProps } from '@/ui/primitives/List';
import { Surface } from '@/ui/primitives/Surface';
import { Text } from '@/ui/primitives/Text';
import type { SxProps, Theme } from '@/ui/primitives/types';
import type { ElementType, ReactElement, ReactNode } from 'react';

const dialogContentActionButtonsSx = {
  '& .MuiButton-root': {
    flex: '0 0 auto',
    width: 'auto',
  },
} as const;

type DialogBodyProps = Omit<StackProps, 'gap' | 'ref'> & {
  children: ReactNode;
  gap?: SpacingToken;
};

export const DialogBody = ({
  children,
  gap = dialogRhythm.sectionGap,
  ...props
}: DialogBodyProps): ReactElement => (
  <Stack gap={gap} {...props}>
    {children}
  </Stack>
);

export const DialogFormBody = ({
  children,
  gap = 'lg',
  ...props
}: DialogBodyProps): ReactElement => (
  <DialogBody gap={gap} {...props}>
    {children}
  </DialogBody>
);

type DialogFormHelperTextProps = {
  align?: 'left' | 'center' | 'right';
  children: ReactNode;
  component?: ElementType;
};

type DialogInlineCodeProps = Omit<BoxProps, 'children' | 'ref'> & {
  children: ReactNode;
  display?: 'inline' | 'inline-flex';
  monospace?: boolean;
};

export const DialogInlineCode = ({
  children,
  component = 'code',
  display = 'inline',
  monospace = true,
  sx,
  ...props
}: DialogInlineCodeProps): ReactElement => (
  <Box
    component={component}
    sx={[
      {
        alignItems: display === 'inline-flex' ? 'center' : undefined,
        backgroundColor: 'action.hover',
        borderRadius: s('xxs'),
        boxDecorationBreak: 'clone',
        display,
        fontFamily: monospace ? fontFamilies.mono : 'inherit',
        overflowWrap: 'anywhere',
        pl: s('sm'),
        pr: s('xs'),
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
  </Box>
);

export const DialogFormHelperText = ({
  align = 'right',
  children,
  component = 'span',
}: DialogFormHelperTextProps): ReactElement => (
  <Text
    role="bodyMuted"
    color="textSecondary"
    component={component}
    sx={{ display: 'block', textAlign: align }}
  >
    {children}
  </Text>
);

type DialogFormChoiceRowProps = {
  control: ReactNode;
  label: ReactNode;
};

export const DialogFormChoiceRow = ({ control, label }: DialogFormChoiceRowProps): ReactElement => (
  <Inline gap="md" justify="flex-end" align="center">
    <Text role="body" component="span" align="right">
      {label}
    </Text>
    {control}
  </Inline>
);

type DialogFormFieldRowProps = Omit<BoxProps, 'children' | 'ref'> & {
  children: ReactNode;
};

export const DialogFormFieldRow = ({
  children,
  sx,
  ...props
}: DialogFormFieldRowProps): ReactElement => (
  <Box
    sx={[
      {
        alignItems: 'start',
        columnGap: s('md'),
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        rowGap: s('xl'),
        '& > *': {
          minWidth: 0,
        },
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
  </Box>
);

type DialogFormActionRowProps = {
  children: ReactNode;
};

export const DialogFormActionRow = ({ children }: DialogFormActionRowProps): ReactElement => (
  <Inline gap="sm" justify="flex-end" align="center" wrap="wrap" sx={dialogContentActionButtonsSx}>
    {children}
  </Inline>
);

type DialogFormSplitActionRowProps = {
  action: ReactNode;
  children: ReactNode;
};

export const DialogFormSplitActionRow = ({
  action,
  children,
}: DialogFormSplitActionRowProps): ReactElement => (
  <Inline gap="sm" align="center" justify="space-between">
    <Text role="body" color="textSecondary" sx={{ flex: 1 }}>
      {children}
    </Text>
    {action}
  </Inline>
);

export const DialogListBody = ({
  children,
  gap = 'md',
  sx,
  ...props
}: DialogBodyProps): ReactElement => (
  <DialogBody
    gap={gap}
    align="stretch"
    sx={[
      {
        mt: s('none'),
        width: '100%',
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
  </DialogBody>
);

type DialogWorkflowBodyProps = Omit<BoxProps, 'children' | 'ref'> & {
  children: ReactNode;
  sideRail: ReactNode;
};

type DialogComparisonGridProps = Omit<BoxProps, 'children' | 'ref'> & {
  children: ReactNode;
  columnCount: number;
  gap?: SpacingToken;
};

export const DialogComparisonGrid = ({
  children,
  columnCount,
  gap = 'lg',
  sx,
  ...props
}: DialogComparisonGridProps): ReactElement => (
  <Box
    sx={[
      {
        display: 'grid',
        gap: s(gap),
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${Math.max(1, columnCount).toString()}, minmax(0, 1fr))`,
        },
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
  </Box>
);

type DialogComparisonGridNoteProps = Omit<StackProps, 'children' | 'gap' | 'ref'> & {
  children: ReactNode;
  columnIndex?: number | null;
  gap?: SpacingToken;
};

export const DialogComparisonGridNote = ({
  children,
  columnIndex,
  gap = 'none',
  sx,
  ...props
}: DialogComparisonGridNoteProps): ReactElement => (
  <Stack
    gap={gap}
    sx={[
      {
        alignItems: 'flex-end',
        gridColumn: {
          xs: '1 / -1',
          sm:
            typeof columnIndex === 'number' && columnIndex >= 0
              ? `${(columnIndex + 1).toString()} / span 1`
              : undefined,
        },
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
  </Stack>
);

export const DialogWorkflowBody = ({
  children,
  sideRail,
  sx,
  ...props
}: DialogWorkflowBodyProps): ReactElement => (
  <Box
    sx={[
      {
        alignItems: 'start',
        display: 'grid',
        gap: s(dialogRhythm.workflowGap),
        gridTemplateColumns: '1fr',
        '& > *': {
          minWidth: 0,
        },
        [`@media (min-width: 56rem)`]: {
          gridTemplateColumns: 'minmax(0, 1fr) minmax(16rem, 18.5rem)',
        },
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
    {sideRail}
  </Box>
);

type DialogListSectionHeaderProps = Omit<BoxProps, 'children' | 'ref'> & {
  children: ReactNode;
  endSlot?: ReactNode;
};

export const DialogListSectionHeader = ({
  children,
  endSlot,
  sx,
  ...props
}: DialogListSectionHeaderProps): ReactElement => (
  <Box
    sx={[
      {
        alignItems: 'center',
        display: 'flex',
        gap: s('xs'),
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
    {endSlot}
  </Box>
);

type DialogListContentStackProps = DialogBodyProps & {
  flushTop?: boolean;
};

export const DialogListContentStack = ({
  children,
  flushTop = false,
  gap = dialogRhythm.sectionContentGap,
  sx,
  ...props
}: DialogListContentStackProps): ReactElement => (
  <Stack
    gap={gap}
    sx={[
      {
        mt: s(flushTop ? 'none' : dialogRhythm.sectionContentGap),
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
  </Stack>
);

type DialogListActionRowProps = Omit<BoxProps, 'children' | 'ref'> & {
  children: ReactNode;
};

export const DialogListActionRow = ({
  children,
  sx,
  ...props
}: DialogListActionRowProps): ReactElement => (
  <Box
    sx={[
      {
        display: 'flex',
        flexWrap: 'wrap',
        gap: s('sm'),
        justifyContent: 'flex-start',
        mt: s(dialogRhythm.sectionContentGap),
        '& .MuiButton-root': {
          flex: '0 0 auto',
          width: 'auto',
        },
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
  </Box>
);

type DialogSelectableListProps = Omit<ListProps, 'children' | 'ref'> & {
  children: ReactNode;
};

export const DialogSelectableList = ({
  children,
  dense = true,
  sx,
  ...props
}: DialogSelectableListProps): ReactElement => (
  <List
    dense={dense}
    sx={[
      {
        py: s('none'),
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
  </List>
);

type DialogSectionProps = Omit<StackProps, 'gap' | 'ref'> & {
  children: ReactNode;
  gap?: SpacingToken;
};

export const DialogSection = ({
  children,
  gap = dialogRhythm.sectionContentGap,
  ...props
}: DialogSectionProps): ReactElement => (
  <Stack gap={gap} {...props}>
    {children}
  </Stack>
);

type DialogInfoFieldProps = Omit<DialogSectionProps, 'children'> & {
  children: ReactNode;
  label: ReactNode;
  valueColor?: 'textPrimary' | 'textSecondary';
  valueSx?: SxProps<Theme>;
};

export const DialogInfoField = ({
  children,
  gap = 'xs',
  label,
  valueColor = 'textPrimary',
  valueSx,
  ...props
}: DialogInfoFieldProps): ReactElement => (
  <DialogSection gap={gap} {...props}>
    <Text role="label">{label}</Text>
    <Text role="body" color={valueColor} sx={valueSx}>
      {children}
    </Text>
  </DialogSection>
);

type DialogSidePanelProps = Omit<StackProps, 'gap' | 'ref'> & {
  children: ReactNode;
  gap?: SpacingToken;
};

export const DialogSidePanel = ({
  children,
  gap = 'none',
  sx,
  ...props
}: DialogSidePanelProps): ReactElement => (
  <Surface
    background="secondary"
    padding={null}
    radius="md"
    sx={[
      {
        alignSelf: 'start',
        backgroundColor: (theme) =>
          isLightTheme(theme) ? `${resolveSemanticColor(theme, 'surface')} !important` : undefined,
        overflow: 'hidden',
        width: '100%',
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    <Stack gap={gap} align="stretch" {...props}>
      {children}
    </Stack>
  </Surface>
);

type DialogSidePanelSectionProps = Omit<StackProps, 'gap' | 'ref'> & {
  children: ReactNode;
  divider?: boolean;
  gap?: SpacingToken;
};

export const DialogSidePanelSection = ({
  children,
  divider = false,
  gap = dialogRhythm.sectionContentGap,
  sx,
  ...props
}: DialogSidePanelSectionProps): ReactElement => (
  <Stack
    gap={gap}
    align="stretch"
    sx={[
      {
        px: s('xl'),
        py: s('xl'),
      },
      divider
        ? {
            borderTop: (theme) => `1px solid ${resolveSemanticColor(theme, 'border')}`,
            mt: s('sm'),
            pt: s('2xl'),
          }
        : null,
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  >
    {children}
  </Stack>
);

type DialogSidePanelTitleProps = {
  children: ReactNode;
  sx?: SxProps<Theme>;
};

export const DialogSidePanelTitle = ({ children, sx }: DialogSidePanelTitleProps): ReactElement => (
  <Text
    role="bodyStrong"
    sx={[
      {
        fontSize: '1.125rem',
        fontWeight: 500,
        lineHeight: 1.3,
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    {children}
  </Text>
);

export const DialogSideRail = DialogSidePanel;
export const DialogSideRailSection = DialogSidePanelSection;
